import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";
import { SparklesCore } from "../components/Sparkles.jsx";

/* Welcome — the morning briefing.
   Editorial layout: dateline → headline → CTAs → "Today's Brief"
   preview (real product surface, diverse examples) → "How it works"
   as a 3-step storyboard. */

const SITE_URL =
  typeof window !== "undefined" ? window.location.origin : "https://communityninja.ai";

const WELCOME_SOFTWARE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Community Ninja",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: SITE_URL,
  description:
    "Community Ninja monitors Reddit everyday and finds relevant conversations about your product, market, competitors, customers, and pain points, so you can engage where it counts.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  featureList: [
    "Daily Reddit monitoring across the subreddits your market lives in",
    "Relevance scoring against your product, customers, and competitors",
    "Short rationales that explain why each thread is worth a reply",
    "Subreddit-aware suggestions for engaging without being spammy",
  ],
};

const WELCOME_FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What does Community Ninja do?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Community Ninja monitors Reddit on a schedule and on demand, scores every new post and comment against your business profile, and surfaces the small set of conversations worth your attention each day.",
      },
    },
    {
      "@type": "Question",
      name: "Is this just keyword alerts?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. Keyword matches are the starting point. Community Ninja ranks threads by intent, recency, and fit to your product, then explains why each one is worth a reply. Most matches get filtered out before they reach you.",
      },
    },
    {
      "@type": "Question",
      name: "Does Community Ninja post on Reddit for me?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Never. The product is read-only by design. You decide which threads to engage with and how. We surface the signal and stay out of the conversation.",
      },
    },
    {
      "@type": "Question",
      name: "Who is Community Ninja for?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Anyone who needs to find the right Reddit conversations: founders, marketers, indie hackers, growth and product teams, agencies, researchers, sales and lead-gen teams, and community managers.",
      },
    },
  ],
};

const WELCOME_JSONLD = [WELCOME_SOFTWARE_JSONLD, WELCOME_FAQ_JSONLD];

function WelcomeScreen({ onStart, onBuildRadar, onExample, onLogin, theme, onToggleTheme }) {
  const [prompt, setPrompt] = React.useState("");
  const [scrolled, setScrolled] = React.useState(false);
  const heroInputRef = React.useRef(null);

  // Empty box → kick off the guided onboarding (describe step). Anything typed
  // → skip straight to building the radar with that seed. onBuildRadar handles
  // both cases; onStart is kept as a fallback for older callers.
  const build = (seed) => {
    const value = (typeof seed === "string" ? seed : prompt).trim();
    if (onBuildRadar) {
      onBuildRadar(value);
    } else if (onStart) {
      onStart();
    }
  };

  // Nav CTA + brand click → focus the hero prompt so people land in the box.
  const focusHero = () => {
    if (heroInputRef.current) {
      heroInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => heroInputRef.current && heroInputRef.current.focus(), 420);
    }
  };

  // Smooth-scroll to an in-page section from the nav links.
  const scrollToId = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Drop a subtle border under the sticky nav once the page moves. The welcome
  // screen may scroll on the window or on its own container depending on the
  // app shell, so watch both and react to whichever moves.
  React.useEffect(() => {
    const root = document.querySelector(".welcome-v4");
    const read = () =>
      setScrolled(
        window.scrollY > 10 || (root ? root.scrollTop > 10 : false)
      );
    read();
    window.addEventListener("scroll", read, { passive: true });
    if (root) root.addEventListener("scroll", read, { passive: true });
    return () => {
      window.removeEventListener("scroll", read);
      if (root) root.removeEventListener("scroll", read);
    };
  }, []);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const frame = window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });

    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="welcome welcome-v4 screen-enter">
      {/* Core component — sparkles fill the whole page and rise from the bottom */}
      <div className="wv-sparkles" aria-hidden="true">
        <SparklesCore
          background="transparent"
          minSize={0.2}
          maxSize={0.8}
          particleDensity={900}
          className="w-full h-full"
          particleColor="var(--sparkle-color, #FFFFFF)"
        />
      </div>
      <Seo
        title="Reddit Monitoring for Customer Discovery"
        description="Community Ninja monitors Reddit every morning and finds relevant conversations about your product, market, competitors, customers, and pain points, so you can engage where it counts."
        keywords={[
          "reddit monitoring",
          "customer discovery",
          "social listening",
          "reddit lead generation",
          "competitor tracking",
          "product research",
          "market research",
          "community-led growth",
          "reddit alerts",
        ]}
        jsonLd={WELCOME_JSONLD}
      />
      <nav className={"wv-nav " + (scrolled ? "is-scrolled" : "")}>
        <div className="wv-nav-inner">
          <button className="wv-brand" onClick={focusHero} aria-label="Community Ninja">
            <img src="/assets/icon.png" alt="Community Ninja" className="wv-brand-icon" style={{ width: 32, height: 32, borderRadius: 8, background: "none" }} />
            <span className="wv-brand-name">Community Ninja</span>
          </button>

          <div className="wv-nav-center">
            <Link to="/why-it-works" className="wv-nav-link">
              Why it works
            </Link>
            <Link to="/how-it-works" className="wv-nav-link">How it works</Link>
            <a
              className="wv-nav-appstore"
              href="https://apps.apple.com/us/app/community-ninja-signals/id6776220645"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Community Ninja Signals on the App Store"
            >
              <img src="/assets/appstoreicon.svg" alt="Download on the App Store" className="wv-nav-appstore-img" />
            </a>
          </div>

          <div className="wv-nav-cta">
            {onToggleTheme && (
              <button
                className="theme-toggle welcome-theme-toggle"
                onClick={onToggleTheme}
                aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
                title={theme === "dark" ? "Light mode" : "Dark mode"}
              >
                {theme === "dark"
                  ? <window.I.Sun size={14} />
                  : <window.I.Moon size={14} />}
              </button>
            )}
            {onLogin && (
              <button className="wv-nav-signin" onClick={onLogin}>
                Sign in
              </button>
            )}
          </div>
        </div>
      </nav>

      <div className="wv4-hero">

        <h1 className="wv4-headline">
          Find conversations asking for{" "}
          <span className="wv4-serif wv4-headline-highlight">what you offer</span>
        </h1>
        <p className="wv4-sub">
          We surface hight-intent conversations asking for your tools or services.
        </p>

        <form
          className="wv4-prompt"
          onSubmit={(e) => { e.preventDefault(); build(); }}
        >
          <div className="wv4-prompt-row">
            <input
              ref={heroInputRef}
              className="wv4-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Website URL or describe it..."
              aria-label="Website URL or describe it..."

            />
            <button className="btn btn-primary btn-lg wv4-prompt-btn" type="submit">
              <window.I.Sparkles size={15} />
              Build my radar
            </button>
          </div>
          <div className="wv4-prompt-help">
            <span className="wv4-prompt-help-left">
              We'll infer your intent, audience, subreddits, and more.
            </span>
          </div>
        </form>

        <div className="wv-trust">
          <span className="wv-trust-avatars" aria-hidden="true">
            <img className="wv-trust-av" src="/assets/one.png" alt="" />
            <img className="wv-trust-av" src="/assets/two.png" alt="" />
            <img className="wv-trust-av" src="/assets/three.png" alt="" />
          </span>
          <span className="wv-trust-stars" aria-hidden="true">★★★★★</span>
          <span className="wv-trust-text">
            Loved by marketers, founders &amp; growth teams
          </span>
        </div>

        <a
          className="wv-trust-appstore"
          href="https://apps.apple.com/us/app/community-ninja-signals/id6776220645"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download Community Ninja Signals on the App Store"
        >
          <img src="/assets/appstoreicon.svg" alt="Download on the App Store" className="wv-trust-appstore-img" />
        </a>
      </div>

      <VideoDemo />
      <BriefPreview onExample={onExample} />
      <HowItWorks />
      <SocialProof />
      <CtaBand onBuild={build} />

      {window.LegalFooter && <window.LegalFooter variant="welcome" />}
    </div>
  );
}

/* ---------- Results / social proof — metrics + testimonials ---------- */
function SocialProof() {
  const metrics = [
    { big: "8", unit: "hrs", lab: "saved per week vs. manual Reddit monitoring" },
    { big: "3", unit: "×", lab: "more high-intent threads surfaced per scan" },
    { big: "5", unit: "mins", lab: "from sign-up to your first ranked brief" },
  ];
  const quotes = [
    {
      stars: "★★★★★",
      lead: "We were checking five subreddits by hand every morning. Now the brief lands and we ",
      em: "only open threads worth replying to.",
      tail: " Closed two deals from it last month.",
      av: "/assets/two.png",
      nm: "Jared V.B",
      rl: "Founder, real-estate SaaS",
    },
    {
      stars: "★★★★★",
      lead: "The fit score is the magic. ",
      em: "It tells me why a thread matters",
      tail: " before I click, so my team stops wasting replies on people who were never going to buy. Closed three deal from it last month.",
      av: "/assets/one.png",
      nm: "Jordan K.",
      rl: "Head of Growth, B2B tooling",
    },
  ];

  return (
    <section className="wv-proof" id="wv-proof">
      <header className="wv-proof-head">
        <span className="wv-proof-eyebrow">Why teams switch</span>
        <h2 className="wv-proof-title">
          Less scrolling. <span className="wv4-serif">More pipeline.</span>
        </h2>
      </header>

      <div className="wv-metrics">
        {metrics.map((m) => (
          <div className="wv-metric" key={m.lab}>
            <div className="wv-metric-big">
              {m.big}
              <span className="wv-metric-unit">{m.unit}</span>
            </div>
            <div className="wv-metric-lab">{m.lab}</div>
          </div>
        ))}
      </div>

      <div className="wv-quotes">
        {quotes.map((q) => (
          <figure className="wv-quote" key={q.nm}>
            <div className="wv-quote-stars" aria-hidden="true">{q.stars}</div>
            <blockquote className="wv-quote-text">
              {q.lead}
              <span className="wv-quote-em">{q.em}</span>
              {q.tail}
            </blockquote>
            <figcaption className="wv-quote-by">
              <img className="wv-quote-av" src={q.av} alt="" />
              <span>
                <span className="wv-quote-nm">{q.nm}</span>
                <span className="wv-quote-rl">{q.rl}</span>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

/* ---------- Final call to action — dark band with its own prompt ---------- */
function CtaBand({ onBuild }) {
  const [value, setValue] = React.useState("");
  return (
    <section className="wv-cta-band">
      <span className="wv-cta-eyebrow">Start in seconds</span>
      <p className="wv-cta-sub">
        Paste your site and we'll build your radar in seconds
      </p>
      <form
        className="wv-cta-form"
        onSubmit={(e) => { e.preventDefault(); onBuild(value); }}
      >
        <input
          className="wv-cta-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Paste your website, product, or topic…"
          aria-label="Paste your website, product, or topic"
        />
        <button className="btn btn-primary btn-lg wv-cta-btn" type="submit">
          Build my radar
          <window.I.ArrowRight size={15} />
        </button>
      </form>
      <div className="wv-cta-note">Free to start · no card · edit everything later</div>
    </section>
  );
}

/* ---------- Product walkthrough video — sits above the brief ---------- */
function VideoDemo() {
  return (
    <section className="wv5-demo" id="wv-demo">
      <header className="wv5-how-head">
      </header>
      <figure className="wv5-how-video">
        <div className="wv5-how-video-frame">
          <video
            className="wv5-how-video-el"
            src="https://communityninja.s3.us-east-2.amazonaws.com/blue_walk.mp4"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-label="Community Ninja product walkthrough"
          />
          <span className="wv5-how-video-glow" aria-hidden="true" />
        </div>
        <figcaption className="wv5-how-video-cap">
          Deep intent analysis into actionable shortlist
        </figcaption>
      </figure>
    </section>
  );
}

/* ---------- "Today's Brief" — diverse, cross-industry sample ---------- */
function BriefPreview({ onExample }) {
  const [expandedIndex, setExpandedIndex] = React.useState(-1);
  const items = [
    {
      n: "01",
      tag: "HIGH INTENT",
      tier: "high",
      sub: "r/realtors",
      ago: "3h",
      title:
        "Realtor looking for better ways to promote listings locally",
      why:
        "The thread is about getting more exposure for new listings without paying for another lead platform, which lines up directly with your product's core value.",
      match: "91% match",
      comments: "24 comments",
      upvotes: "47 upvotes",
    },
    {
      n: "02",
      tag: "WATCHING",
      tier: "new",
      sub: "r/Entrepreneur",
      ago: "11h",
      title:
        "Founder exploring whether community-based distribution can outperform paid listing boosts",
      why:
        "This one is earlier-stage but promising: they're evaluating organic local reach vs paid promotion and are open to tooling that makes community posting sustainable.",
      match: "79% match",
      comments: "14 comments",
      upvotes: "28 upvotes",
    },
    {
      n: "03",
      tag: "HIGH INTENT",
      tier: "high",
      sub: "r/smallbusiness",
      ago: "8h",
      title:
        "Small team asking how to stop copy-pasting every new property post by hand",
      why:
        "This is strong operational pain: same asset, many communities, repeated every week. That's exactly the kind of repetitive workflow your product removes.",
      match: "86% match",
      comments: "18 comments",
      extra: "+ 2 more matches in this thread",
      upvotes: "39 upvotes",
    },
  ];

  return (
    <section className="wv4-brief-section" id="wv-output">
      <header className="wv-output-head">
        <span className="wv-output-eyebrow">The daily output</span>
        <h2 className="wv-output-title">
          A ranked shortlist of conversations{" "}
          <span className="wv4-serif">worth your time.</span>
        </h2>
      </header>

      <div className="wv4-brief">
        <header className="wv4-brief-head">
          <span className="wv4-brief-label">Today's Brief</span>
          <span className="wv4-brief-rule" />
          <span className="wv4-brief-meta">
            <strong>3</strong> highlights &nbsp;·&nbsp; <strong>23</strong> total
            &nbsp;·&nbsp; <strong>14</strong> subreddits
          </span>
        </header>

        <ol className="wv4-brief-list">
          {items.map((it, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <li
                className={"wv4-brief-row " + (isExpanded ? "is-expanded" : "")}
                key={it.n}
              >
                <button
                  type="button"
                  className="wv4-brief-hit"
                  onClick={() => setExpandedIndex(isExpanded ? -1 : index)}
                  aria-expanded={isExpanded}
                >
                  <div className="wv4-brief-num">{it.n}</div>
                  <div className="wv4-brief-body">
                    <div className="wv4-brief-kicker">
                      <span className={"wv4-brief-tag tier-" + it.tier}>
                        {it.tag}
                      </span>
                      <span className="wv4-brief-sub">{it.sub}</span>
                      <span className="wv4-brief-sep">·</span>
                      <span className="wv4-brief-ago">{it.ago} ago</span>
                    </div>
                    <div className="wv4-brief-title">{it.title}</div>
                    {it.extra && <div className="wv4-brief-extra">{it.extra}</div>}
                    <div className="wv4-brief-foot">
                      <span>{it.match}</span>
                      <span className="wv4-brief-foot-sep">·</span>
                      <span>{it.comments}</span>
                      <span className="wv4-brief-foot-sep">·</span>
                      <span>{it.upvotes}</span>
                    </div>
                    <div className="wv4-brief-why-wrap">
                      <div className="wv4-brief-why">
                        <span className="wv4-brief-why-label">Why this fits your profile</span>
                        <p className="wv4-brief-why-text">{it.why}</p>
                      </div>
                    </div>
                  </div>
                  <div className="wv4-brief-arrow" aria-hidden="true">
                    <window.I.ArrowUpRight size={14} />
                  </div>
                </button>
              </li>
            );
          })}
        </ol>

        <footer className="wv4-brief-footer">
          <button className="wv4-brief-cta" onClick={onExample}>
            Open sample brief
            <window.I.ArrowRight size={12} />
          </button>
          <span className="wv4-brief-footer-meta">
            Your feed updates on schedule or whenever you run a scan
          </span>
        </footer>
      </div>
    </section>
  );
}

/* ============================================================
   How it works — 3-step storyboard with live visual per step
   ============================================================ */

function HowItWorks() {
  const fullText =
    "Community Ninja helps B2B founders find real buyer intent on Reddit before competitors do.";
  const subs = ["r/realtors", "r/SaaS", "r/smallbusiness", "r/Entrepreneur"];

  const [phase, setPhase] = React.useState(0);
  const [typed, setTyped] = React.useState("");
  const [typeComplete, setTypeComplete] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const ts = [];
    const at = (ms, fn) =>
      ts.push(setTimeout(() => !cancelled && fn(), ms));

    setPhase(0);
    setTyped("");

    for (let i = 0; i <= fullText.length; i++) {
      at(400 + i * 22, () => setTyped(fullText.slice(0, i)));
    }
    const tEnd = 400 + fullText.length * 22;
    at(tEnd + 80, () => setTypeComplete(true));
    at(tEnd + 700, () => setPhase(1));
    at(tEnd + 2400, () => setPhase(2));

    return () => {
      cancelled = true;
      ts.forEach(clearTimeout);
    };
  }, []);

  const steps = [
    {
      n: "01",
      title: "Teach us your signal",
      body:
        "Describe what your business does, who it helps, and the problems, goals, or trigger moments that show up right before someone starts looking. A few clear lines are enough.",
      art: <ArtDescribe typed={typed} active={phase >= 0} typeComplete={typeComplete} />,
    },
    {
      n: "02",
      title: "We scan for live intent",
      body:
        "Community Ninja reads posts and comments across the subreddits that matter, separates curiosity from buying energy, and filters out empty keyword matches.",
      art: <ArtListen subs={subs} active={phase >= 1} />,
    },
    {
      n: "03",
      title: "Act on the right threads",
      body:
        "The shortlist arrives with an intent headline, fit score, and why it matters, so you can jump in quickly with context instead of starting from scratch.",
      art: <ArtReply active={phase >= 2} />,
    },
  ];

  return (
    <section className="wv5-how" id="wv-how">
      <header className="wv5-how-head">
        <span className="wv5-how-eyebrow">How it works</span>
      </header>

      <h2 className="wv5-how-title wv5-how-title--steps">
        Turn Reddit into a repeatable{" "}
        <span className="wv4-serif">signal loop</span>.
      </h2>

      <ol className="wv5-steps">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            {i > 0 && (
              <li className="wv5-connect" aria-hidden="true">
                <span
                  className={
                    "wv5-connect-fill " + (phase >= i ? "is-on" : "")
                  }
                />
              </li>
            )}
            <li className={"wv5-step " + (phase >= i ? "is-active" : "")}>
              <div className="wv5-step-head">
                <span className="wv5-step-num">{s.n}</span>
                <span className="wv5-step-marker" />
              </div>
              <div className="wv5-step-art">{s.art}</div>
              <h3 className="wv5-step-title">{s.title}</h3>
              <p className="wv5-step-body">{s.body}</p>
            </li>
          </React.Fragment>
        ))}
      </ol>
    </section>
  );
}

/* ---------- Step 1 art: typewriter input ---------- */
function ArtDescribe({ typed, active, typeComplete }) {
  return (
    <div className={"art-describe " + (active ? "is-active" : "")}>
      <div className="art-describe-label">Your product</div>
      <div className="art-describe-text">
        {typed}
        {!typeComplete && <span className="art-describe-cursor" />}
      </div>
    </div>
  );
}

/* ---------- Step 2 art: scanning radar w/ subreddit chips ---------- */
function ArtListen({ subs, active }) {
  return (
    <div className={"art-listen " + (active ? "is-active" : "")}>
      <div className="art-listen-radar" aria-hidden="true">
        <span className="art-listen-ring r1" />
        <span className="art-listen-ring r2" />
        <span className="art-listen-ring r3" />
        <span className="art-listen-core" />
        <span className="art-listen-sweep" />
      </div>
      <div className="art-listen-chips">
        {subs.map((s, i) => (
          <span
            key={s}
            className={"art-listen-chip " + (active ? "is-in" : "")}
            style={{ transitionDelay: `${i * 140}ms` }}
          >
            <span className="art-listen-chip-dot" />
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------- Step 3 art: mini match card with why ---------- */
function ArtReply({ active }) {
  return (
    <div className={"art-reply " + (active ? "is-active" : "")}>
      <div className="art-reply-kicker">
        <span className="art-reply-tier">HIGH INTENT</span>
        <span className="art-reply-sub">r/realtors</span>
        <span className="art-reply-score">93%</span>
      </div>
      <div className="art-reply-title">
        Realtor looking for better ways to promote listings locally
      </div>
      <div className="art-reply-why">
        <span className="art-reply-why-label">Why</span>
        <span className="art-reply-why-text">
          Explicit distribution pain · clear fit
        </span>
      </div>
      <div className="art-reply-cta">
        Open thread
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
          strokeLinejoin="round"><path d="M7 17 17 7M8 7h9v9" /></svg>
      </div>
    </div>
  );
}

window.WelcomeScreen = WelcomeScreen;
